import type { ChangeEvent, MouseEventHandler, TouchEventHandler } from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { search } from '@/services/ws-search/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { useNavigate } from '@/js/libs/router';
import { Media } from '@/js/libs/ui';
import { Icons, Input } from '@/js/libs/ui/src/V2';
import { NotActive, useRequest } from '@/js/libs/use-request';

import { useNavigationContext } from '../../../context.tsx';
import { SearchList } from './SearchList.tsx';
import { RootWrapper, StyledTooltip } from './styles.ts';

const stopPropagation: TouchEventHandler<HTMLInputElement> & MouseEventHandler<HTMLInputElement> = (e) => {
  e.stopPropagation();
};

type Props = {
  autofocus: boolean;
};

export const SearchInput = memo(function SearchInput({ autofocus }: Props) {
  const [t] = useTranslation(['backend', 'common']);
  const { colors } = useTheme();
  const isDesktop = useMediaQuery({ query: Media.lgMin });
  const nav = useNavigate();

  const { isCollapsedNav, collapseMainNav } = useNavigationContext();

  const [value, setValue] = useState('');
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const handleChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  });

  const handleClear = useCallbackRef(() => {
    setValue('');
  });

  const handleNavigate = useCallbackRef((path: string) => {
    handleClear();
    nav(path);
    if (!isDesktop && !isCollapsedNav) {
      collapseMainNav();
    }
  });

  const { data$, pending$ } = useRequest({
    apiMethod: search,
    params: value ? { query: value } : NotActive,
    debounce: 200,
  });

  const searchRequestPending = useSubscribe(pending$);
  const searchRequestData = useSubscribe(data$);

  const data = useMemo(() => (value ? searchRequestData?.data : undefined), [searchRequestData?.data, value]);

  useEffect(() => {
    if (inputRef && autofocus) {
      inputRef.focus({ preventScroll: true });
    }
  }, [autofocus, inputRef]);

  return (
    <StyledTooltip
      renderTooltipWithoutWrapper
      containerComponentProps={{ maxHeight: '90vh' }}
      WrapperComponent={RootWrapper}
      arrow={false}
      placement='right-start'
      type='popper'
      visible={!!value}
      tooltip={<SearchList loading={searchRequestPending} data={data} search={value} onNavigate={handleNavigate} onClose={handleClear} />}>
      <Input
        dataTestId='global-search-input'
        ref={setInputRef}
        autoComplete='off'
        value={value}
        icon={<Icons.Search color={colors.grey100_1} />}
        placeholder={t('common:PLACEHOLDER_SEARCH')}
        onChange={handleChange}
        onMouseDown={stopPropagation}
        onPointerDown={stopPropagation}
        onTouchStart={stopPropagation}
      />
    </StyledTooltip>
  );
});
